import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import {
  initializeAuth,
  getAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  type Auth,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyD6iGI0D2K_v26HJxtK7feLAhf_o0gjC7o",
  authDomain: "video-shorts.firebaseapp.com",
  databaseURL: "https://video-shorts.firebaseio.com",
  projectId: "video-shorts",
  storageBucket: "video-shorts.appspot.com",
  messagingSenderId: "259402629053",
  appId: "1:259402629053:web:82f6c32ae35e5f1c219cd4"
};

let cachedAuth: Auth | null = null;

// Passed explicitly to signInWithPopup() (see lib/client_auth.ts). Kept out
// of the Auth instance itself — see getFirebaseAuth() below for why.
export const popupResolver = browserPopupRedirectResolver;

// Lazy on purpose, and constructed WITHOUT a popupRedirectResolver.
//
// getAuth() wires in browserPopupRedirectResolver by default, and that
// resolver's `_shouldInitProactively` is true on every mobile browser, Safari
// and iOS. On those (i.e. most of our traffic) the SDK eagerly boots its
// cross-domain iframe on every page load: apis.google.com/js/api.js (gapi) →
// video-shorts.firebaseapp.com/__/auth/iframe(.js) → identitytoolkit
// getProjectConfig — a ~1.8s critical-path chain, ~150KB, plus a set of
// third-party Google cookies, on pages that never show login UI (Aug 2026
// Lighthouse audit). The idle-deferral in authUserContext didn't help because
// requestIdleCallback fires within milliseconds on an idle main thread.
//
// The resolver is only needed for signInWithPopup/Redirect and for picking up
// redirect results at startup. We use popup sign-in exclusively and never
// signInWithRedirect, so omitting it here loses nothing: the iframe now loads
// lazily inside signInWithPopup(auth, provider, popupResolver) when a user
// actually clicks a social login button. Persisted sessions still restore on
// load via IndexedDB/localStorage exactly as before (same persistence
// hierarchy getAuth() uses), so onAuthStateChanged behaves identically.
//
// getAuth(app) is used as a fallback only if something already initialized
// Auth for this app (initializeAuth throws "already-initialized" otherwise);
// every call site in the app should go through this function so that can't
// happen in practice.
export function getFirebaseAuth(): Auth {
  if (!cachedAuth) {
    const app: FirebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp();
    try {
      cachedAuth = initializeAuth(app, {
        persistence: [indexedDBLocalPersistence, browserLocalPersistence],
        popupRedirectResolver: undefined,
      });
    } catch {
      cachedAuth = getAuth(app);
    }
  }
  return cachedAuth;
}
